/**
 * navmeshStatus — the ONE place that turns `window.__NAVMESH__` into a UI verdict
 * (ENG-1050). Read by the outliner's NAVMESH row (dot + meta) and by the
 * viewport chip, so the two can never disagree about whether a navmesh exists.
 *
 * It exists because "is there a navmesh?" was previously answered by each caller
 * spelling out `state === 'applied'` — which is how a chip came to read "Navmesh
 * ready" on a scene whose navmesh was, from the user's point of view, unusable.
 * One rule set, four states, no caller-local interpretation.
 *
 * Pure: no DOM, no window, no THREE. The caller passes the state object in.
 */

/**
 * @param {{state?:string,nodeCount?:number,size?:number,url?:string,error?:string}|null} nav
 *   `window.__NAVMESH__`, verbatim (null when nothing has published one).
 * @param {{kind?:string,pct?:number}|null} [bake]  the navmesh row's bake state
 *   from bakeKind's rowBakeState + the shared bake %, when a bake is running.
 * @returns {{built:boolean, tone:'ok'|'warn'|'error', label:string, meta:string,
 *            detail:string, kind:string}}
 *   `built` = there is a navmesh the runtime can path on RIGHT NOW. `tone` drives
 *   the dot colour. `meta` is the outliner's right-aligned column. `detail` is the
 *   chip's second line — always says what to do, never just what is wrong.
 */
export function navmeshRowStatus(nav, bake = null) {
  // A running bake outranks the current state: the artifact is on its way, and
  // reporting "not built" through a build is how a user concludes it failed.
  if (bake?.kind === 'baking') {
    const pct = Number.isFinite(bake.pct) ? ` ${Math.round(bake.pct)}%` : ''
    return {
      built: false, tone: 'warn', kind: 'baking',
      label: 'Building navmesh', meta: `BAKING${pct}`,
      detail: 'The bake is running — the navmesh appears when it finishes.',
    }
  }

  const state = nav?.state ?? null
  const nodes = Number.isFinite(nav?.nodeCount) ? nav.nodeCount : null
  const nodeText = nodes != null ? `${nodes} nodes` : null

  switch (state) {
    case 'applied':
      return {
        built: true, tone: 'ok', kind: 'baked',
        // The meta stays SHORT: it shares the outliner row with the label, and a
        // long one truncated "Navmesh" to "Navm…". The count belongs in the
        // detail (the dot's hover) and the row's own details panel.
        label: 'Navmesh', meta: 'BAKED',
        detail: ['Baked for this environment.', nodeText].filter(Boolean).join(' '),
      }
    case 'studio-floor':
      // Not from a bake, but genuinely navigable — a green dot, with the meta
      // naming the source so it is never mistaken for a baked environment.
      return {
        built: true, tone: 'ok', kind: 'studio-floor',
        label: 'Navmesh', meta: 'STUDIO FLOOR',
        detail: ['No environment: the studio floor grid is the walkable area.', nodeText]
          .filter(Boolean).join(' '),
      }
    case 'pending':
      return {
        built: false, tone: 'warn', kind: 'loading',
        label: 'Navmesh', meta: 'LOADING',
        detail: 'Fetching the baked navmesh for this environment.',
      }
    case 'error':
      return {
        built: false, tone: 'error', kind: 'error',
        label: 'Navmesh failed', meta: 'ERROR',
        detail: nav?.error ? String(nav.error) : 'The navmesh failed to load.',
      }
    case 'no-cache':
      return {
        built: false, tone: 'error', kind: 'not-built',
        label: 'Navmesh not built', meta: 'NOT BUILT',
        detail: 'This environment has no baked navmesh. Build it from Build ▸ Build Navmesh.',
      }
    default:
      // No state at all — nothing has published one (no scene yet, or an
      // environment mid-swap). Reported as not built rather than as an error:
      // there is nothing broken, there is simply nothing to walk on.
      return {
        built: false, tone: 'error', kind: 'not-built',
        label: 'Navmesh not built', meta: 'NOT BUILT',
        detail: 'No navmesh for this scene. Build it from Build ▸ Build Navmesh.',
      }
  }
}

/**
 * The same verdict shape for the navmesh's SIBLING baked artifacts — the voxel
 * grid and the collision mesh. They have no studio-floor equivalent and no bake
 * progress of their own, so the rules are simpler; what matters is that they read
 * the same way in the outliner and are never silently absent.
 *
 * @param {{state?:string,url?:string}|null} art  window.__VOXELS__ / __COLLISION__
 * @param {string} label  row label when it exists ("Voxels")
 * @param {string} what   what it is, for the not-built sentence ("voxel grid")
 */
export function artifactRowStatus(art, label, what) {
  const state = art?.state ?? null
  if (state === 'applied') {
    return { built: true, tone: 'ok', label, meta: 'BAKED', detail: 'Baked for this environment.' }
  }
  if (state === 'pending') {
    return { built: false, tone: 'warn', label, meta: 'LOADING', detail: 'Fetching the ' + what + '.' }
  }
  if (state === 'error') {
    return {
      built: false, tone: 'error', label: label + ' failed', meta: 'ERROR',
      detail: art?.error ? String(art.error) : 'The ' + what + ' failed to load.',
    }
  }
  return {
    built: false, tone: 'error', label: label + ' not built', meta: 'NOT BUILT',
    detail: 'This environment has no baked ' + what + '. Build it from the navmesh row.',
  }
}
